import { CombinedError } from "urql";
import type { ImageCropBox } from "@/components/ui/image-cropper/ImageCropper";
import { standardAuthFetch } from "@/data-access/core/authenticatedFetch";
import { dataServiceBaseUrl } from "@/data-access/core/constants";
import type { ResultOf } from "@/data-access/graphql";
import {
  cropLayoutTemplateAssetMutationString,
  IngestLayoutTemplateAssetFromUrl,
  UploadLayoutTemplateAssetMutationString,
} from "@/data-access/scheduled-briefings/queries/template-layout-asset-queries";
import { client } from "@/lib/urqlProvider";

export interface TemplateLayoutAsset {
  assetId: string;
  url: string;
}

interface ImageCropEdit extends ImageCropBox {
  /** Scales the crop down to this many pixels wide. */
  targetWidth?: number;
}

export type TemplateLayoutAssetRepositoryError =
  | { code: "INVALID_ASSET"; message: string }
  | { code: "REQUEST_FAILURE"; message: string };

export type TemplateLayoutAssetRepositoryResult<T> =
  | { success: true; data: T }
  | { success: false; error: TemplateLayoutAssetRepositoryError };

type TemplateLayoutAssetMutationPayload = ResultOf<
  typeof IngestLayoutTemplateAssetFromUrl
>["ingestLayoutTemplateAssetFromUrl"];

type AssetGraphqlJson<TField extends string> = {
  errors?: readonly { message: string; extensions?: Record<string, unknown> }[];
  data?: Partial<Record<TField, TemplateLayoutAssetMutationPayload | null>>;
};

const toAssetResult = <TField extends string>(
  result: AssetGraphqlJson<TField>,
  field: TField,
) => ({
  error: result.errors?.length
    ? new CombinedError({ graphQLErrors: [...result.errors] })
    : undefined,
  payload: result.data?.[field] ?? undefined,
});

const requestFailure = (
  message: string,
): TemplateLayoutAssetRepositoryError => ({
  code: "REQUEST_FAILURE",
  message,
});

const mapAssetGraphQLError = (
  error: Pick<CombinedError, "graphQLErrors" | "networkError">,
): TemplateLayoutAssetRepositoryError => {
  for (const graphQLError of error.graphQLErrors) {
    if (graphQLError.extensions?.code === "INVALID_ASSET") {
      return { code: "INVALID_ASSET", message: graphQLError.message };
    }
  }

  return requestFailure(
    error.networkError?.message ||
      error.graphQLErrors[0]?.message ||
      "Image request failed.",
  );
};

const mapAssetPayload = (
  payload: TemplateLayoutAssetMutationPayload,
): TemplateLayoutAssetRepositoryResult<TemplateLayoutAsset> => {
  if (payload.__typename === "OperationInfo") {
    const message =
      payload.messages.map((entry) => entry.message).join(" ") ||
      "Image request failed.";
    const invalidAsset = payload.messages.find(
      (entry) => entry.code === "INVALID_ASSET",
    );
    return {
      success: false,
      error: invalidAsset
        ? { code: "INVALID_ASSET", message }
        : requestFailure(message),
    };
  }

  return {
    success: true,
    data: { assetId: payload.assetId, url: payload.url },
  };
};

const runAssetRequest = async (
  action: "upload" | "ingest" | "crop",
  execute: () => Promise<{
    error: CombinedError | undefined;
    payload: TemplateLayoutAssetMutationPayload | undefined;
  }>,
): Promise<TemplateLayoutAssetRepositoryResult<TemplateLayoutAsset>> => {
  try {
    const { error, payload } = await execute();
    if (error !== undefined) {
      return { success: false, error: mapAssetGraphQLError(error) };
    }
    if (payload === undefined) {
      return {
        success: false,
        error: requestFailure(`Image ${action} returned no data.`),
      };
    }
    return mapAssetPayload(payload);
  } catch (error) {
    return {
      success: false,
      error: requestFailure(
        error instanceof Error ? error.message : `Image ${action} failed.`,
      ),
    };
  }
};

/** @public */
export const uploadGraphQLTemplateLayoutAsset = (
  file: File,
): Promise<TemplateLayoutAssetRepositoryResult<TemplateLayoutAsset>> =>
  runAssetRequest("upload", async () => {
    const formData = new FormData();
    formData.append(
      "operations",
      JSON.stringify({
        query: UploadLayoutTemplateAssetMutationString,
        variables: { file: null },
      }),
    );
    formData.append("map", JSON.stringify({ "1": ["variables.file"] }));
    formData.append("1", file, file.name.split(/[/\\]/).pop() || "image");

    const response = await standardAuthFetch(`${dataServiceBaseUrl}/graphql/`, {
      method: "POST",
      body: formData,
    });
    const result =
      (await response.json()) as AssetGraphqlJson<"uploadLayoutTemplateAsset">;
    return toAssetResult(result, "uploadLayoutTemplateAsset");
  });

/**
 * Crops a copy of an asset to the box, scales it down to `targetWidth` pixels when set, and
 * stores it as a new asset; the source is unchanged. Posts outside urql, so the caller shows
 * the error and `signal` cancels the request.
 */
export const cropGraphQLTemplateLayoutAsset = (
  assetId: string,
  edit: ImageCropEdit,
  signal?: AbortSignal,
): Promise<TemplateLayoutAssetRepositoryResult<TemplateLayoutAsset>> =>
  runAssetRequest("crop", async () => {
    const response = await standardAuthFetch(`${dataServiceBaseUrl}/graphql/`, {
      method: "POST",
      body: JSON.stringify({
        query: cropLayoutTemplateAssetMutationString(
          edit.targetWidth !== undefined,
        ),
        variables: { assetId, ...edit },
      }),
      signal,
    });
    const result =
      (await response.json()) as AssetGraphqlJson<"cropLayoutTemplateAsset">;
    return toAssetResult(result, "cropLayoutTemplateAsset");
  });

/** Copies a remote image into an asset. Does not store the remote URL. */
export const ingestGraphQLTemplateLayoutAssetFromUrl = (
  url: string,
): Promise<TemplateLayoutAssetRepositoryResult<TemplateLayoutAsset>> =>
  runAssetRequest("ingest", async () => {
    const result = await client
      .mutation(IngestLayoutTemplateAssetFromUrl, { url })
      .toPromise();
    return {
      error: result.error,
      payload: result.data?.ingestLayoutTemplateAssetFromUrl,
    };
  });
